import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import installmentService, {
  InstallmentPlan,
  InstallmentFrequency,
  InstallmentScheduleStatus,
  CreateInstallmentPlanDTO,
} from '../../../../services/installmentService';
import { salesInvoiceService, SalesInvoiceDTO } from '../../../../services/salesInvoiceService';
import { useAuth } from '../../../../context/AuthContext';
import { Permissions } from '../../../../constants/Permissions';
import { getServerErrorMessage } from '../../../../utils/errorUtils';
import '../../../../styles/ImsTheme.css';
import './InstallmentPlanPage.css';
import { ArrowLeft, CalendarClock, Save, Wallet, CircleCheck } from 'lucide-react';

const MIN_INSTALLMENTS = 2;
const MAX_INSTALLMENTS = 60;

interface PlanForm {
  numberOfInstallments: number | '';
  frequency: InstallmentFrequency;
  firstDueDate: string;
}

interface FieldErrors {
  numberOfInstallments?: string;
  firstDueDate?: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const InstallmentPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const [invoice, setInvoice] = useState<SalesInvoiceDTO | null>(null);
  const [plan, setPlan] = useState<InstallmentPlan | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<PlanForm>({
    numberOfInstallments: 3,
    frequency: InstallmentFrequency.Monthly,
    firstDueDate: todayIso(),
  });

  const canCreate = hasPermission(Permissions.SalesInvoices.Edit);

  const load = useCallback(async () => {
    if (!id) return;
    setLoadingData(true);
    setError(null);
    try {
      const [invoiceResponse, existingPlan] = await Promise.all([
        salesInvoiceService.getSalesInvoiceById(id),
        installmentService.getByInvoiceId(Number(id)),
      ]);
      setInvoice(invoiceResponse.data.data ?? null);
      setPlan(existingPlan);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('installment.failedToLoad'));
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    let finalValue: any;
    if (name === 'frequency') {
      finalValue = Number(value) as InstallmentFrequency;
    } else if (name === 'numberOfInstallments') {
      finalValue = value === '' ? '' : Number(value);
    } else {
      finalValue = value;
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (formData.numberOfInstallments === '' || formData.numberOfInstallments === null) {
      errors.numberOfInstallments = t('installment.formValidation.countRequired');
      isValid = false;
    } else if (Number(formData.numberOfInstallments) < MIN_INSTALLMENTS) {
      errors.numberOfInstallments = t('installment.formValidation.countMin', { min: MIN_INSTALLMENTS });
      isValid = false;
    } else if (Number(formData.numberOfInstallments) > MAX_INSTALLMENTS) {
      errors.numberOfInstallments = t('installment.formValidation.countMax', { max: MAX_INSTALLMENTS });
      isValid = false;
    }

    if (!formData.firstDueDate) {
      errors.firstDueDate = t('installment.formValidation.firstDueDateRequired');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    const dto: CreateInstallmentPlanDTO = {
      numberOfInstallments: Number(formData.numberOfInstallments),
      frequency: formData.frequency,
      // Date-only input; send it as a UTC instant so the backend's due-date maths
      // starts from the day the user actually picked.
      firstDueDate: new Date(`${formData.firstDueDate}T00:00:00Z`).toISOString(),
    };

    setSaving(true);
    try {
      const created = await installmentService.create(Number(id), dto);
      setPlan(created ?? null);
      setSuccessMessage(t('installment.createSuccess'));
    } catch (err) {
      setError(getServerErrorMessage(err) || t('installment.failedToCreate'));
    } finally {
      setSaving(false);
    }
  };

  const formatMoney = (value: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2 }).format(value);

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value));

  const statusBadge = (status: InstallmentScheduleStatus) => {
    if (status === InstallmentScheduleStatus.Paid) {
      return <span className="ims-badge ims-badge-approved">{t('installment.status.paid')}</span>;
    }
    if (status === InstallmentScheduleStatus.PartiallyPaid) {
      return <span className="ims-badge ims-badge-pending">{t('installment.status.partiallyPaid')}</span>;
    }
    if (status === InstallmentScheduleStatus.Overdue) {
      return <span className="ims-badge ims-badge-rejected">{t('installment.status.overdue')}</span>;
    }
    return <span className="ims-badge ims-badge-draft">{t('installment.status.pending')}</span>;
  };

  const frequencyLabel = (frequency: InstallmentFrequency) =>
    frequency === InstallmentFrequency.Weekly ? t('installment.weekly') : t('installment.monthly');

  if (loadingData) {
    return (
      <Layout>
        <div className="edit-container installment-plan-page">
          <div className="ims-loading">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  const scheduledTotal = plan?.schedule.reduce((sum, row) => sum + row.amount, 0) ?? 0;
  const paidTotal = plan?.schedule.reduce((sum, row) => sum + row.paidAmount, 0) ?? 0;

  return (
    <Layout>
      <div className="edit-container installment-plan-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate(`/sales/sales-invoices/${id}`)}
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('installment.title')}
              {invoice ? ` — ${invoice.invoiceNumber}` : ''}
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {invoice && (
          <div className="ims-stats-row">
            <div className="ims-stat-card">
              <div className="ims-stat-icon installment-icon-total"><Wallet size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{formatMoney(invoice.totalAmount)}</span>
                <span className="ims-stat-label">{t('installment.invoiceTotal')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon installment-icon-paid"><CircleCheck size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{formatMoney(invoice.amountPaid)}</span>
                <span className="ims-stat-label">{t('installment.amountPaid')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon installment-icon-due"><CalendarClock size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{formatMoney(invoice.balanceDue)}</span>
                <span className="ims-stat-label">{t('installment.balanceDue')}</span>
              </div>
            </div>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            {plan ? (
              <div className="edit-card">
                <div className="edit-card-header">
                  <h2 className="edit-card-title">{t('installment.scheduleTitle')}</h2>
                  <span className="installment-plan-meta">
                    {t('installment.planSummary', {
                      count: plan.numberOfInstallments,
                      frequency: frequencyLabel(plan.frequency),
                    })}
                  </span>
                </div>
                <div className="edit-card-body">
                  <div className="ims-table-scroll">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th className="ims-th" style={{ minWidth: 60 }}>#</th>
                          <th className="ims-th" style={{ minWidth: 140 }}>{t('installment.dueDate')}</th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 120 }}>{t('installment.amount')}</th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 120 }}>{t('installment.paidAmount')}</th>
                          <th className="ims-th" style={{ minWidth: 130 }}>{t('common.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plan.schedule.map((row) => (
                          <tr key={row.id} className="ims-tr">
                            <td className="ims-td">{row.sequenceNumber}</td>
                            <td className="ims-td">{formatDate(row.dueDate)}</td>
                            <td className="ims-td ims-td--num">{formatMoney(row.amount)}</td>
                            <td className="ims-td ims-td--num">{formatMoney(row.paidAmount)}</td>
                            <td className="ims-td">{statusBadge(row.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="ims-td" colSpan={2}>{t('installment.total')}</td>
                          <td className="ims-td ims-td--num">{formatMoney(scheduledTotal)}</td>
                          <td className="ims-td ims-td--num">{formatMoney(paidTotal)}</td>
                          <td className="ims-td" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="edit-card">
                <div className="edit-card-header">
                  <h2 className="edit-card-title">{t('installment.createPlan')}</h2>
                </div>
                <div className="edit-card-body">
                  {!canCreate ? (
                    <p className="installment-empty">{t('installment.noPlanReadOnly')}</p>
                  ) : (
                    <form id="page-form" onSubmit={handleSubmit} noValidate>
                      <div className="form-group">
                        <label className="form-label">
                          {t('installment.numberOfInstallments')} <span className="required">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-input ${fieldErrors.numberOfInstallments ? 'is-invalid' : ''}`}
                          name="numberOfInstallments"
                          value={formData.numberOfInstallments}
                          onChange={handleInputChange}
                          step="1"
                          min={MIN_INSTALLMENTS}
                          max={MAX_INSTALLMENTS}
                        />
                        {fieldErrors.numberOfInstallments && (
                          <small className="form-error">{fieldErrors.numberOfInstallments}</small>
                        )}
                        {!fieldErrors.numberOfInstallments && (
                          <small className="form-helper">
                            {t('installment.numberOfInstallmentsHint', { min: MIN_INSTALLMENTS, max: MAX_INSTALLMENTS })}
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          {t('installment.frequency')} <span className="required">*</span>
                        </label>
                        <select
                          className="form-select"
                          name="frequency"
                          value={formData.frequency}
                          onChange={handleInputChange}
                        >
                          <option value={InstallmentFrequency.Weekly}>{t('installment.weekly')}</option>
                          <option value={InstallmentFrequency.Monthly}>{t('installment.monthly')}</option>
                        </select>
                        <small className="form-helper">{t('installment.frequencyHint')}</small>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          {t('installment.firstDueDate')} <span className="required">*</span>
                        </label>
                        <input
                          type="date"
                          className={`form-input ${fieldErrors.firstDueDate ? 'is-invalid' : ''}`}
                          name="firstDueDate"
                          value={formData.firstDueDate}
                          onChange={handleInputChange}
                        />
                        {fieldErrors.firstDueDate && <small className="form-error">{fieldErrors.firstDueDate}</small>}
                        {!fieldErrors.firstDueDate && (
                          <small className="form-helper">{t('installment.firstDueDateHint')}</small>
                        )}
                      </div>

                      <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                        {saving
                          ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                          : <><Save size={15} strokeWidth={2} />{t('installment.createPlan')}</>}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('installment.createPlan')}</strong>
                  <p>{t('installment.help.createPlan')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('installment.frequency')}</strong>
                  <p>{t('installment.help.frequency')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('installment.paidAmount')}</strong>
                  <p>{t('installment.help.payments')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InstallmentPlanPage;
